import { FC, ReactNode, useState } from 'react';
import { AnalyseOut } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { getGenreId } from '../../shared/common/translationMappers/genreIdMapper';

type Props = {
    analysis: AnalyseOut;
};

const AnalyseTable: FC<Props> = ({
    analysis: {
        amount,
        newProducts,
        newProductsPercentage,
        additionalCopies,
        additionalCopiesPercentage,
        longQueue,
        longQueuePercentage,
        badges,
        ageCategory,
        genres,
        fiction,
        fictionPercentage,
        nonFiction,
        nonFictionPercentage
    }
}) => {
    const [showMoreGenres, setShowMoreGenres] = useState(false);
    const [showMoreBadges, setShowMoreBadges] = useState(false);
    const { t } = useTranslation();
    function toggleMoreGenres() {
        setShowMoreGenres(!showMoreGenres);
    }

    function toggleMoreBadges() {
        setShowMoreBadges(!showMoreBadges);
    }

    let badgesTable: ReactNode;
    if (badges) {
        let moreBadges: ReactNode;
        let moreBadgesLink: ReactNode;
        if (badges.length > 5) {
            if (showMoreBadges) {
                moreBadges = badges.slice(5).map((badge, i) => (
                    <tr key={badge.name || i}>
                        <td>
                            <div className="bd bgreen">{badge.name}</div>
                        </td>
                        <td className="td-amount">
                            <span className="marked smoky">{badge.amount}</span>
                        </td>
                        <td className="td-percentage">
                            <span className="marked smoky">{badge.percentage}%</span>
                        </td>
                    </tr>
                ));
            }

            const moreBadgeText = showMoreBadges ? t('components.analyse.less') : t('components.analyse.more');

            moreBadgesLink = (
                <tr>
                    <td colSpan={3}>
                        <a onClick={toggleMoreBadges} className="lk-more">
                            <div>{moreBadgeText}</div>
                        </a>
                    </td>
                </tr>
            );
        }
        badgesTable = (
            <table>
                <tbody>
                    {badges.slice(0, 5).map((badge, i) => (
                        <tr key={badge.name || i}>
                            <td>
                                <div className="bd bgreen">{badge.name}</div>
                            </td>
                            <td className="td-amount">
                                <span className="marked smoky">{badge.amount}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{badge.percentage}%</span>
                            </td>
                        </tr>
                    ))}
                    {moreBadges}
                    {moreBadgesLink}
                </tbody>
            </table>
        );
    }

    let ageCategoriesTable: ReactNode | null;
    if (ageCategory) {
        ageCategoriesTable = (
            <table>
                <tbody>
                    <tr>
                        <th colSpan={3}>{t('components.analyse.ageCategories')}</th>
                    </tr>
                    {ageCategory.map((category, i) => (
                        <tr key={category.name || i}>
                            <td>
                                {t(`filters:ageCategory.${category.name}`, {
                                    defaultValue: category.name
                                })}
                            </td>
                            <td className="td-amount">
                                <span className="marked smoky">{category.amount}</span>
                            </td>
                            <td>
                                <span className="marked smoky">{category.percentage}%</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    let genresTable: ReactNode;
    if (genres) {
        let moreGenres: ReactNode | null;
        let moreGenresLink: ReactNode | null;
        if (genres.length > 5) {
            if (showMoreGenres) {
                moreGenres = genres.slice(5, 20).map((genre, i) => (
                    <tr key={genre.name || i}>
                        <td className="td-genre-name">
                            {t(`filters:genre.${getGenreId(genre.genreId ?? '')}`, {
                                defaultValue: genre.name
                            })}
                        </td>
                        <td className="td-amount">
                            <span className="marked smoky">{genre.amount}</span>
                        </td>
                        <td>
                            <span className="marked smoky">{genre.percentage}%</span>
                        </td>
                    </tr>
                ));
            }

            const moreGenresText = showMoreGenres ? t('components.analyse.less') : t('components.analyse.more');
            moreGenresLink = (
                <tr>
                    <td colSpan={3}>
                        <a onClick={toggleMoreGenres} className="lk-more">
                            <div>{moreGenresText}</div>
                        </a>
                    </td>
                </tr>
            );
        }
        genresTable = (
            <table>
                <tbody>
                    <tr>
                        <th colSpan={3}>Genres</th>
                    </tr>
                    {genres.slice(0, 5).map((genre, i) => (
                        <tr key={genre.name || i}>
                            <td className="td-genre-name">
                                {t(`filters:genre.${getGenreId(genre.genreId ?? '')}`, {
                                    defaultValue: genre.name
                                })}
                            </td>
                            <td className="td-amount">
                                <span className="marked smoky">{genre.amount}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{genre.percentage}%</span>
                            </td>
                        </tr>
                    ))}
                    {moreGenres}
                    {moreGenresLink}
                </tbody>
            </table>
        );
    }

    return (
        <tr className={!amount ? 'hidden' : ''}>
            <td>
                <table>
                    <tbody>
                        <tr>
                            <td style={{ paddingLeft: 0 }}>{t('components.analyse.newProducts')}</td>
                            <td className="td-amount">
                                <span className="marked smoky">{newProducts}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{newProductsPercentage}%</span>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ paddingLeft: 0 }}>{t('components.analyse.additionalCopies')}</td>
                            <td className="td-amount">
                                <span className="marked smoky">{additionalCopies}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{additionalCopiesPercentage}%</span>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ paddingLeft: 0 }}>
                                <div className="bd marked borange"> {t('components.analyse.longQueue')}</div>
                            </td>
                            <td className="td-amount">
                                <span className="marked smoky">{longQueue}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{longQueuePercentage}%</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </td>
            <td>{badgesTable}</td>
            <td>{ageCategoriesTable}</td>
            <td>{genresTable}</td>
            <td>
                <table>
                    <tbody>
                        <tr>
                            <th colSpan={3}>{t('components.analyse.fictionNonFiction')}</th>
                        </tr>
                        <tr>
                            <td>{t('components.analyse.fiction')}</td>
                            <td className="td-amount">
                                <span className="marked smoky">{fiction}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{fictionPercentage}%</span>
                            </td>
                        </tr>
                        <tr>
                            <td>{t('components.analyse.nonFiction')}</td>
                            <td className="td-amount">
                                <span className="marked smoky">{nonFiction}</span>
                            </td>
                            <td className="td-percentage">
                                <span className="marked smoky">{nonFictionPercentage}%</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </td>
        </tr>
    );
};

export default AnalyseTable;
